import { env } from '../env';

/**
 * A simple logging utility that only logs messages when DEBUG_MODE is true
 */
export const logger = {
  log: (...args: any[]) => {
    if (env.DEBUG_MODE) {
      console.log(...args);
    }
  },
  
  error: (...args: any[]) => {
    if (env.DEBUG_MODE) {
      console.error(...args);
    }
  },
  
  warn: (...args: any[]) => {
    if (env.DEBUG_MODE) {
      console.warn(...args);
    }
  },
  
  info: (...args: any[]) => {
    if (env.DEBUG_MODE) {
      console.info(...args);
    }
  }
};

/**
 * Suppresses all console output from third-party libraries in production mode
 * Call this once at app startup to prevent libraries from logging
 */
export const suppressConsoleInProduction = () => {
  if (!env.DEBUG_MODE) {
    // Store original console methods
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };

    // Create a global error handler to catch and suppress CORS errors
    window.addEventListener('error', (event) => {
      const errorMessage = event.message || '';
      
      // Check if it's a CORS or third-party error we want to suppress
      if (
        errorMessage.includes('CORS') ||
        errorMessage.includes('Access-Control-Allow-Origin') ||
        errorMessage.includes('Failed to load resource') ||
        errorMessage.includes('linkedin.com') ||
        errorMessage.includes('facebook.com') ||
        errorMessage.includes('insights') ||
        errorMessage.includes('tracking')
      ) {
        event.preventDefault();
        return false;
      }
      
      return true;
    }, true);
    
    // Override console methods to filter out third-party library messages
    console.log = (...args: any[]) => {
      // Convert all arguments to strings for checking
      const message = args.map(arg => String(arg || '')).join(' ');
      
      // Filter out common development-related messages
      if (
        message.includes('Download the React DevTools') ||
        message.includes('react-dom') ||
        message.includes('React Router') ||
        message.includes('%c') || // Styled console logs
        message.includes('linkedin.com') ||
        message.includes('facebook.com') ||
        message.includes('POST') ||
        message.includes('GET http') ||
        message.includes('health') ||
        message.includes('ERR_CONNECTION_REFUSED') ||
        message.includes('insight') ||
        message.includes('CORS')
      ) {
        return; // Skip these messages
      }
      
      // Only show our app's intentional logs
      if (env.DEBUG_MODE) {
        originalConsole.log(...args);
      }
    };

    console.warn = (...args: any[]) => {
      // Convert all arguments to strings for checking
      const message = args.map(arg => String(arg || '')).join(' ');
      
      // Filter out common warnings
      if (
        message.includes('React Router') ||
        message.includes('Warning:') ||
        message.includes('deprecated') ||
        message.includes('CORS') ||
        message.includes('Access-Control-Allow-Origin') ||
        message.includes('http://localhost') ||
        message.includes('linkedin') ||
        message.includes('facebook')
      ) {
        return; // Skip these warnings
      }
      
      // Only pass through warnings that might be critical
      if (env.DEBUG_MODE) {
        originalConsole.warn(...args);
      }
    };

    console.error = (...args: any[]) => {
      // Convert all arguments to strings for checking
      const message = args.map(arg => String(arg || '')).join(' ');
      
      // Filter out common non-critical errors
      if (
        message.includes('React Router') ||
        message.includes('Warning:') ||
        message.includes('index.js:') ||
        message.includes('DevTools') ||
        message.includes('scheduler') ||
        message.includes('CORS') ||
        message.includes('Access-Control-Allow-Origin') ||
        message.includes('linkedin.com') ||
        message.includes('facebook.com') ||
        message.includes('ERR_CONNECTION_REFUSED') ||
        message.includes('404') ||
        message.includes('health') ||
        message.includes('insight') ||
        message.includes('net::ERR') ||
        message.includes('POST https:')
      ) {
        return; // Skip these errors
      }
      
      // Only log true errors that would affect functionality
      if (
        message.includes('Fatal') || 
        message.includes('Uncaught') || 
        message.includes('Exception') ||
        env.DEBUG_MODE
      ) {
        originalConsole.error(...args);
      }
    };

    console.info = (...args: any[]) => {
      // Silently discard all info messages in production
      if (env.DEBUG_MODE) {
        originalConsole.info(...args);
      }
    };
  }
};
